/**
 * OmegaBrainCard — shows the live weight-history + per-vector hit-rates from the
 * Hedge Fund upgrades layer. Drop into Settings → Automation Thinking.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VECTOR_LABELS = {
  direction_logit: "Direction",
  iv_rank: "IV Rank",
  greeks_alignment: "Greeks",
  capital_efficiency: "Cap Eff",
  whale_flow: "Whale Flow",
  regime_hmm: "Regime",
  tech_structure: "Tech",
  liquidity: "Liquidity",
  news_sentiment: "News (TEA)",
  event_risk: "Event Risk",
  bayesian_prior: "Bayesian",
  meta_confidence: "Meta Conf",
};

export default function OmegaBrainCard() {
  const [weights, setWeights] = useState(null);
  const [attr, setAttr] = useState(null);
  const [tuning, setTuning] = useState(false);
  const [lastTune, setLastTune] = useState(null);

  const load = () => {
    axios.get(`${API}/hedge/weights/latest`).then(r => setWeights(r.data)).catch(() => {});
    axios.get(`${API}/hedge/attribution/summary`).then(r => setAttr(r.data)).catch(() => {});
  };
  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, []);

  const runTune = async () => {
    setTuning(true);
    try {
      const r = await axios.post(`${API}/hedge/auto-tune`);
      setLastTune(r.data);
      load();
    } catch (e) {
      setLastTune({ ok: false, error: e.response?.data?.detail || e.message });
    }
    setTuning(false);
  };

  const cur = weights?.current || {};
  const def = weights?.default || {};
  const byVec = attr?.byVector || {};

  return (
    <div className="card" data-testid="omega-brain-card" style={{ borderColor: "rgba(167,139,250,0.3)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
        <div className="sh" style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
          Omega Brain
          <span className="tag t-pur" style={{ fontSize: 8 }}>12-VECTOR + HEDGE FUND</span>
        </div>
        <button
          data-testid="run-auto-tune-btn"
          onClick={runTune}
          disabled={tuning}
          style={{
            padding: "6px 14px", borderRadius: 14, border: "1px solid rgba(167,139,250,0.4)",
            background: tuning ? "#3a3f47" : "rgba(167,139,250,0.14)",
            color: "#a78bfa", fontSize: 11, fontWeight: 700,
            cursor: tuning ? "default" : "pointer",
          }}
        >{tuning ? "Tuning…" : "Run Auto-Tune"}</button>
      </div>

      {attr && (
        <div style={{ padding: 8, background: "rgba(255,255,255,0.02)", borderRadius: 8, marginBottom: 10, fontSize: 11, color: "var(--dim)" }}>
          <strong style={{ color: "var(--text)" }}>{attr.totalTrades}</strong> closed trades logged ·
          win rate <strong style={{ color: attr.winRate >= 60 ? "#4ade80" : attr.winRate >= 50 ? "#fbbf24" : "#f87171" }}>{attr.winRate}%</strong>
          {weights?.samples > 0 && <> · last tune <strong>{weights.samples} samples</strong></>}
        </div>
      )}

      {lastTune && (
        <div data-testid="last-tune-result" style={{ marginBottom: 10, padding: 8, borderRadius: 8, background: lastTune.ok ? "rgba(74,222,128,0.08)" : "rgba(239,68,68,0.08)", border: `1px solid ${lastTune.ok ? "rgba(74,222,128,0.3)" : "rgba(239,68,68,0.3)"}`, fontSize: 11, color: lastTune.ok ? "#4ade80" : "#f87171" }}>
          {lastTune.ok ? `✓ Tuned ${lastTune.samples} samples` : `✗ ${lastTune.reason || lastTune.error}`}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 80px 80px", gap: "4px 10px", fontSize: 11 }}>
        <div style={{ fontSize: 9, color: "var(--dim)", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>Vector</div>
        <div style={{ fontSize: 9, color: "var(--dim)", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, textAlign: "right" }}>Weight</div>
        <div style={{ fontSize: 9, color: "var(--dim)", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, textAlign: "right" }}>WR @ High</div>
        <div style={{ fontSize: 9, color: "var(--dim)", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, textAlign: "right" }}>WR @ Low</div>

        {Object.keys(def).map(vec => {
          const w = cur[vec] ?? def[vec];
          const dw = w - (def[vec] || 0);
          const st = byVec[vec] || {};
          return (
            <div key={vec} style={{ display: "contents" }} data-testid={`brain-row-${vec}`}>
              <div style={{ color: "var(--text)" }}>{VECTOR_LABELS[vec] || vec}</div>
              <div style={{ textAlign: "right", color: "var(--text)", fontFamily: "monospace" }}>
                {(w * 100).toFixed(1)}%
                {Math.abs(dw) > 0.002 && (
                  <span style={{ marginLeft: 4, fontSize: 9, color: dw > 0 ? "#4ade80" : "#f87171" }}>
                    {dw > 0 ? "+" : ""}{(dw * 100).toFixed(1)}
                  </span>
                )}
              </div>
              <div style={{ textAlign: "right", color: st.winRateHigh != null && st.winRateHigh >= 60 ? "#4ade80" : "var(--dim)", fontFamily: "monospace", fontSize: 10 }}>
                {st.winRateHigh != null ? `${st.winRateHigh}%` : "—"}
                <span style={{ fontSize: 8, marginLeft: 2, color: "var(--dim)" }}>(n={st.highs || 0})</span>
              </div>
              <div style={{ textAlign: "right", color: st.winRateLow != null && st.winRateLow >= 60 ? "#4ade80" : "var(--dim)", fontFamily: "monospace", fontSize: 10 }}>
                {st.winRateLow != null ? `${st.winRateLow}%` : "—"}
                <span style={{ fontSize: 8, marginLeft: 2, color: "var(--dim)" }}>(n={st.lows || 0})</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
